import { useMsal } from '@azure/msal-react';
import useSWR from 'swr';
import { useCallback, useMemo } from 'react';
import { IUserData } from '@shared/interfaces';
import { IPermission, UserRole } from '@modules/admin/AdminTools/Roles/shared';
import { Roles, ROLES_PRIORITY } from '@shared/constants/constants';
import { SERVICES } from '../constants/apiConfig';

export const useUserAccount = () => {
  const { accounts } = useMsal();

  const { data: userData, isLoading: isUserDataLoading } = useSWR<IUserData>(`/${SERVICES.USER}/users/email?email=${accounts[0]?.username}`, { revalidateOnMount: undefined });
  const { data: userPermission, isLoading: isUserPermissionLoading } = useSWR<IPermission>(userData?.id && `/${SERVICES.USER}/roleAssignment/permission/${userData?.id}`, {
    fallback: { permissions: [] },
    revalidateOnMount: undefined,
  });
  const { 
    data: userRoles = [],
    isLoading: isUserRolesLoading,
  } = useSWR<UserRole[]>(userData?.id && `/${SERVICES.USER}/roleAssignment/role/${userData?.id}`, {
    fallback: [{ name : '' }],
    revalidateOnMount: undefined,
  });

  const userHasRoles = useCallback(
    (roleNames: string[]) => userRoles.some((userRole) => roleNames.includes(userRole.name)),
    [userRoles]
  );

  const userHasPermissions = useCallback(
    (permissions: string[]) => userPermission?.permissions.some(permission => permissions.includes(permission)),
    [userRoles]
  );

  const isLoading = useMemo(
    () => isUserDataLoading || isUserRolesLoading || isUserPermissionLoading,
    [isUserDataLoading, isUserRolesLoading, isUserPermissionLoading]
  );

  const updatedUserRoles = useMemo(() => {
    const rolesWithPriority = userRoles.map((role) => ({
      ...role,
      priority: ROLES_PRIORITY[role.name as keyof typeof ROLES_PRIORITY] ?? Object.keys(ROLES_PRIORITY).length + 1
    }));

    const sortedArray = rolesWithPriority.sort((a, b) => a.priority - b.priority);
    const updatedRoles = sortedArray.map((userRole) => userRole.name) as (keyof Roles)[];

    return updatedRoles;
  }, [userRoles]);
  
  return {
    isLoading,
    userId: userData?.id,
    username: accounts[0]?.username,
    isAdmin: userData?.isAdmin ?? false,
    userPermissions: userPermission?.permissions ?? [],
    userRoles: updatedUserRoles,
    userHasRoles,
    userHasPermissions,
  };
};
